import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  NavBar,
  Steps,
  Card,
  Button,
  Form,
  TextArea,
  Toast,
  Dialog,
  ImageViewer,
  Badge,
  Tag,
  Skeleton,
  SwipeAction,
  List,
  SafeArea,
  Space,
  Divider,
} from 'antd-mobile';
import {
  LeftOutline,
  FireFill,
  ExclamationCircleFill,
  ClockCircleFill,
  CheckCircleFill,
  CloseCircleFill,
  EditSOutline,
  DeleteOutline,
  EyeOutline,
} from 'antd-mobile-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketApi } from '@/api/tickets';
import { useAuthStore } from '@/stores/authStore';
import { formatDate, getStatusText, getLevelText } from '@/utils/formatters';
import type { TicketWithImages } from '@shared/types';

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const ticketId = parseInt(id || '0');
  const [imageVisible, setImageVisible] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [showRectifyForm, setShowRectifyForm] = useState(
    location.state?.openRectify || false
  );

  // 查询工单详情
  const {
    data: ticket,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['ticket', 'detail', ticketId],
    queryFn: async () => {
      const res = await ticketApi.detail(ticketId);
      return res.data.data;
    },
    enabled: !!ticketId,
    staleTime: 1000 * 10,
  });

  // 整改提交 mutation
  const rectifyMutation = useMutation({
    mutationFn: (rectifyText: string) =>
      ticketApi.rectify(ticketId, { rectify_text: rectifyText }),
    onSuccess: () => {
      Toast.show({ icon: 'success', content: '整改提交成功' });
      setShowRectifyForm(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['ticket', 'detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || '提交失败';
      Toast.show({ icon: 'fail', content: msg });
    },
  });

  // 是否可以整改
  const canRectify =
    ticket?.status === 'pending' &&
    (user?.id === ticket?.responder_id || user?.role === 'admin');

  // 是否可以编辑追踪
  const canEditTracking =
    ticket?.status === 'rectified' &&
    (user?.role === 'admin' || user?.id === ticket?.submitter_id);

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      pending: '#ff4d4f',
      rectified: '#2E5BFF',
      verified: '#52c41a',
      closed: '#9CA3AF',
    };
    return map[status] || '#9CA3AF';
  };

  // 获取等级颜色
  const getLevelColor = (level: string) => {
    return level === 'red' ? '#ff4d4f' : '#faad14';
  };

  // 获取步骤索引
  const getStepIndex = (status: string) => {
    const map: Record<string, number> = {
      pending: 0,
      rectified: 1,
      verified: 2,
      closed: 3,
    };
    return map[status] ?? 0;
  };

  // 获取步骤图标
  const getStepIcon = (status: string) => {
    const icons: Record<string, React.ReactNode> = {
      pending: <ClockCircleFill />,
      rectified: <EditSOutline />,
      verified: <CheckCircleFill />,
      closed: <CloseCircleFill />,
    };
    return icons[status] || <ClockCircleFill />;
  };

  // 处理整改提交
  const handleRectify = (values: { rectify_text: string }) => {
    Dialog.confirm({
      title: '确认提交整改',
      content: '提交后将无法修改，请确认改善措施填写完整。',
      confirmText: '确认',
      cancelText: '再想想',
      onConfirm: () => {
        rectifyMutation.mutate(values.rectify_text);
      },
    });
  };

  // 验收通过 mutation
  const verifyMutation = useMutation({
    mutationFn: () => ticketApi.verify(ticketId),
    onSuccess: () => {
      Toast.show({ icon: 'success', content: '验收通过' });
      queryClient.invalidateQueries({ queryKey: ['ticket', 'detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || '验收失败';
      Toast.show({ icon: 'fail', content: msg });
    },
  });

  // 关闭工单 mutation
  const closeMutation = useMutation({
    mutationFn: () => ticketApi.close(ticketId),
    onSuccess: () => {
      Toast.show({ icon: 'success', content: '工单已关闭' });
      queryClient.invalidateQueries({ queryKey: ['ticket', 'detail', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || '关闭失败';
      Toast.show({ icon: 'fail', content: msg });
    },
  });

  // 验收通过
  const handleVerify = () => {
    Dialog.confirm({
      title: '确认验收通过',
      content: '验收通过后，工单将标记为"已验收"状态。',
      confirmText: '确认',
      cancelText: '取消',
      onConfirm: () => { verifyMutation.mutate(); }
    });
  };

  // 关闭工单
  const handleClose = () => {
    Dialog.confirm({
      title: '确认关闭工单',
      content: '关闭后工单将标记为"已关闭"，不再展示在活跃列表中。',
      confirmText: '确认关闭',
      cancelText: '取消',
      onConfirm: () => { closeMutation.mutate(); }
    });
  };

  // 预览图片
  const handlePreviewImage = (index: number) => {
    setImageIndex(index);
    setImageVisible(true);
  };

  if (isLoading) {
    return (
      <div className="ticket-detail-page">
        <SafeArea position="top" />
        <NavBar onBack={() => navigate(-1)} backArrow={<LeftOutline fontSize={20} />}>
          工单详情
        </NavBar>
        <div className="detail-skeleton">
          <Skeleton.Title animated />
          <Skeleton.Paragraph lineCount={6} animated />
        </div>
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="ticket-detail-page">
        <SafeArea position="top" />
        <NavBar onBack={() => navigate(-1)} backArrow={<LeftOutline fontSize={20} />}>
          工单详情
        </NavBar>
        <div className="detail-error">
          <div style={{ fontSize: 64, marginBottom: 16 }}>📡</div>
          <div style={{ fontSize: 16, color: '#6B7280', marginBottom: 16 }}>
            加载失败，请检查网络
          </div>
          <Button onClick={() => { refetch(); }}>重新加载</Button>
        </div>
      </div>
    );
  }

  const images = ticket.images || [];

  return (
    <div className="ticket-detail-page">
      <SafeArea position="top" />

      {/* 顶部导航 */}
      <NavBar
        onBack={() => navigate(-1)}
        backArrow={<LeftOutline fontSize={20} />}
        className="detail-nav"
      >
        工单详情
      </NavBar>

      <div className="detail-content">
        {/* 工单头部信息 */}
        <div className="detail-header-card">
          <div className="detail-header-top">
            <div className="ticket-level-badge" style={{ background: getLevelColor(ticket.level) }}>
              {ticket.level === 'red' ? '🔴' : '🟡'}
              <span>{getLevelText(ticket.level)}</span>
            </div>
            <Tag
              color={getStatusColor(ticket.status)}
              fill="outline"
              className="detail-status-tag"
            >
              {getStatusText(ticket.status)}
            </Tag>
          </div>
          <div className="detail-ticket-no">{ticket.ticket_no}</div>
          <div className="detail-meta-row">
            <span className="detail-meta">
              <span className="meta-icon">👤</span>
              提交人：{ticket.submitter_name}
            </span>
            <span className="detail-meta">
              <span className="meta-icon">🕐</span>
              {formatDate(ticket.submit_time, 'MM-DD HH:mm')}
            </span>
          </div>
        </div>

        {/* 问题描述 */}
        <Card className="detail-card" title="问题描述">
          <div className="detail-description">{ticket.description}</div>
        </Card>

        {/* 责任人信息 */}
        <Card className="detail-card" title="责任人">
          <div className="responder-detail">
            <div className="responder-avatar-large">
              {ticket.responder_name.charAt(0)}
            </div>
            <div className="responder-detail-info">
              <div className="responder-detail-name">{ticket.responder_name}</div>
              <div className="responder-detail-id">ID: {ticket.responder_id}</div>
            </div>
          </div>
        </Card>

        {/* 现场照片 */}
        {images.length > 0 && (
          <Card className="detail-card" title="现场照片">
            <div className="detail-images">
              {images.map((img: any, index: number) => (
                <div
                  key={img.id}
                  className="detail-image-item"
                  onClick={() => handlePreviewImage(index)}
                >
                  <img
                    src={img.image_url}
                    alt={`现场照片 ${index + 1}`}
                    className="detail-image"
                    loading="lazy"
                  />
                  <div className="image-overlay">
                    <EyeOutline fontSize={20} color="white" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 状态时间轴 */}
        <Card className="detail-card" title="处理进度">
          <Steps
            current={getStepIndex(ticket.status)}
            direction="vertical"
            className="detail-steps"
          >
            <Steps.Step
              title="已提交"
              description={
                <div className="step-desc">
                  <div>提交人：{ticket.submitter_name}</div>
                  <div>{formatDate(ticket.submit_time)}</div>
                </div>
              }
              icon={getStepIcon('pending')}
            />
            <Steps.Step
              title="已整改"
              description={
                ticket.rectify_time ? (
                  <div className="step-desc">
                    <div>整改人：{ticket.rectify_user}</div>
                    <div>{formatDate(ticket.rectify_time)}</div>
                  </div>
                ) : (
                  <div className="step-desc-empty">待责任人处理</div>
                )
              }
              icon={getStepIcon('rectified')}
            />
            <Steps.Step
              title="已验收"
              description={
                ticket.verify_time ? (
                  <div className="step-desc">
                    <div>验收人：{ticket.verify_user}</div>
                    <div>{formatDate(ticket.verify_time)}</div>
                  </div>
                ) : (
                  <div className="step-desc-empty">待管理员/巡检员验收</div>
                )
              }
              icon={getStepIcon('verified')}
            />
            <Steps.Step
              title="已关闭"
              description={
                ticket.status === 'closed' ? (
                  <div className="step-desc">
                    <div>工单已归档</div>
                  </div>
                ) : (
                  <div className="step-desc-empty">流程结束后自动关闭</div>
                )
              }
              icon={getStepIcon('closed')}
            />
          </Steps>
        </Card>

        {/* 整改记录 */}
        {ticket.status !== 'pending' && ticket.rectify_text && (
          <Card className="detail-card" title="整改记录">
            <div className="rectify-record">
              <div className="record-section">
                <div className="record-label">改善措施</div>
                <div className="record-content">{ticket.rectify_text}</div>
              </div>
              <div className="record-meta-row">
                <span className="record-meta">
                  <span className="record-meta-label">整改人：</span>
                  {ticket.rectify_user}
                </span>
                <span className="record-meta">
                  <span className="record-meta-label">整改时间：</span>
                  {formatDate(ticket.rectify_time)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* 追踪记录（P2） */}
        {(ticket.preventive_action || ticket.effect_verify) && (
          <Card className="detail-card" title="追踪记录">
            {ticket.preventive_action && (
              <div className="record-section">
                <div className="record-label">预防措施</div>
                <div className="record-content">{ticket.preventive_action}</div>
              </div>
            )}
            {ticket.effect_verify && (
              <div className="record-section">
                <div className="record-label">效果验证</div>
                <div className="record-content">{ticket.effect_verify}</div>
              </div>
            )}
            {ticket.verify_user && (
              <div className="record-meta-row">
                <span className="record-meta">
                  <span className="record-meta-label">验收人：</span>
                  {ticket.verify_user}
                </span>
                <span className="record-meta">
                  <span className="record-meta-label">验收时间：</span>
                  {formatDate(ticket.verify_time)}
                </span>
              </div>
            )}
          </Card>
        )}

        {/* 整改操作区 */}
        {canRectify && (
          <div className="rectify-section">
            {!showRectifyForm ? (
              <Button
                block
                type="submit"
                size="large"
                onClick={() => setShowRectifyForm(true)}
                className="rectify-open-btn"
              >
                <span style={{ color: '#fff' }}>提交整改</span>
              </Button>
            ) : (
              <Card className="rectify-form-card" title="填写改善措施">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleRectify}
                  footer={
                    <div className="rectify-form-footer">
                      <Button
                        type="submit"
                        color="primary"
                        block
                        size="large"
                        loading={rectifyMutation.isPending}
                        className="rectify-submit-btn"
                      >
                        <span style={{ color: '#fff' }}>{rectifyMutation.isPending ? '提交中…' : '确认整改'}</span>
                      </Button>
                      <Button
                        block
                        fill="outline"
                        size="large"
                        onClick={() => {
                          setShowRectifyForm(false);
                          form.resetFields();
                        }}
                        className="rectify-cancel-btn"
                      >
                        取消
                      </Button>
                    </div>
                  }
                >
                  <Form.Item
                    name="rectify_text"
                    rules={[
                      { required: true, message: '请填写改善措施' },
                      { max: 2000, message: '不能超过2000字' },
                    ]}
                  >
                    <TextArea
                      placeholder="请详细描述已采取的改善措施，包括：
? 具体整改内容
? 整改完成时间
? 后续预防措施"
                      rows={5}
                      maxLength={2000}
                      showCount
                      className="rectify-textarea"
                    />
                  </Form.Item>
                </Form>
              </Card>
            )}
          </div>
        )}

        {/* 验收操作区 */}
        {ticket.status === 'rectified' && user?.role === 'admin' && (
          <div className="verify-section">
            <Button
              block
              type="submit"
              size="large"
              loading={verifyMutation.isPending}
              onClick={handleVerify}
              className="verify-btn"
            >
              验收通过
            </Button>
          </div>
        )}

        {/* 关闭操作区（仅管理员） */}
        {['rectified', 'verified'].includes(ticket.status) && user?.role === 'admin' && (
          <div className="close-section">
            <Button
              block
              fill="outline"
              size="large"
              loading={closeMutation.isPending}
              onClick={handleClose}
              className="close-btn"
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f30' }}
            >
              关闭工单
            </Button>
          </div>
        )}

        {/* 追踪编辑区（P2，管理员/巡检员） */}
        {canEditTracking && (
          <div className="tracking-section">
            <Card className="detail-card" title="更新追踪信息">
              <div className="tracking-placeholder">
                <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                <div style={{ fontSize: 14, color: '#9CA3AF' }}>
                  追踪功能（P2）即将上线
                </div>
                <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>
                  管理员可在此填写预防措施和效果验证
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* 底部占位 */}
        <div className="detail-footer" />
      </div>

      {/* 图片预览 */}
      {images.length > 0 && (
        <ImageViewer
          image={images[imageIndex]?.image_url}
          visible={imageVisible}
          onClose={() => setImageVisible(false)}
          
        />
      )}

      <style>{`
        .ticket-detail-page {
          min-height: 100vh;
          background: #F5F7FA;
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
        }

        .detail-nav {
          background: white;
          border-bottom: 1px solid #F0F2F5;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .detail-nav .adm-nav-bar-title {
          font-size: 17px;
          font-weight: 600;
        }

        .detail-content {
          padding: 12px;
        }

        .detail-skeleton {
          padding: 24px;
        }

        .detail-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
          text-align: center;
        }

        /* 头部信息卡片 */
        .detail-header-card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .detail-header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .ticket-level-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 12px;
          border-radius: 20px;
          color: white;
          font-size: 13px;
          font-weight: 600;
        }

        .detail-status-tag {
          font-size: 12px !important;
          padding: 2px 10px !important;
          border-radius: 4px !important;
        }

        .detail-ticket-no {
          font-size: 22px;
          font-weight: 700;
          color: #1F2433;
          font-family: monospace;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }

        .detail-meta-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .detail-meta {
          font-size: 13px;
          color: #6B7280;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .meta-icon {
          font-size: 14px;
        }

        /* 通用卡片 */
        .detail-card {
          margin-bottom: 12px;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .detail-card .adm-card-header {
          font-size: 15px;
          font-weight: 600;
          color: #1F2433;
          padding: 16px 16px 8px;
        }

        .detail-card .adm-card-body {
          padding: 8px 16px 16px;
        }

        /* 问题描述 */
        .detail-description {
          font-size: 15px;
          line-height: 1.8;
          color: #1F2433;
          white-space: pre-wrap;
          word-break: break-word;
        }

        /* 责任人 */
        .responder-detail {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .responder-avatar-large {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 600;
        }

        .responder-detail-info {
          display: flex;
          flex-direction: column;
        }

        .responder-detail-name {
          font-size: 16px;
          font-weight: 600;
          color: #1F2433;
        }

        .responder-detail-id {
          font-size: 12px;
          color: #9CA3AF;
        }

        /* 图片 */
        .detail-images {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .detail-image-item {
          position: relative;
          width: calc(50% - 4px);
          aspect-ratio: 1;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
        }

        .detail-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .detail-image-item:active .image-overlay {
          opacity: 1;
        }

        /* 时间轴 */
        .detail-steps {
          --step-icon-size: 28px;
        }

        .detail-steps .adm-step-icon-container {
          width: 28px;
          height: 28px;
        }

        .detail-steps .adm-step-title {
          font-size: 14px;
          font-weight: 600;
        }

        .step-desc {
          font-size: 13px;
          color: #6B7280;
          line-height: 1.6;
        }

        .step-desc-empty {
          font-size: 13px;
          color: #bbb;
          font-style: italic;
        }

        /* 整改记录 */
        .rectify-record {
          padding: 4px 0;
        }

        .record-section {
          margin-bottom: 16px;
        }

        .record-section:last-child {
          margin-bottom: 0;
        }

        .record-label {
          font-size: 13px;
          font-weight: 600;
          color: #1F2433;
          margin-bottom: 8px;
        }

        .record-content {
          font-size: 14px;
          line-height: 1.7;
          color: #555;
          background: #f8f8f8;
          padding: 12px;
          border-radius: 8px;
          white-space: pre-wrap;
        }

        .record-meta-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #F0F2F5;
        }

        .record-meta {
          font-size: 12px;
          color: #9CA3AF;
        }

        .record-meta-label {
          color: #bbb;
        }

        /* 整改操作区 */
        .rectify-section {
          margin-top: 16px;
          padding: 0 4px;
        }

        .rectify-open-btn {
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          height: 48px;
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .rectify-open-btn:active {
          opacity: 0.9;
        }

        .rectify-form-card {
          margin-bottom: 0;
        }

        .rectify-form-footer {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding-top: 8px;
        }

        .rectify-submit-btn {
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          height: 44px;
          background: linear-gradient(135deg, #2E5BFF 0%, #6B8DFF 100%);
          border: none;
        }

        .rectify-cancel-btn {
          border-radius: 10px;
          font-size: 15px;
          height: 44px;
          color: #6B7280;
          border-color: #e0e0e0;
        }

        .rectify-textarea {
          border-radius: 10px;
          font-size: 15px;
          line-height: 1.7;
        }

        .rectify-textarea .adm-text-area-element {
          padding: 12px;
        }

        /* 追踪区 */
        .tracking-section {
          margin-top: 12px;
          padding: 0 4px;
        }

        .tracking-placeholder {
          text-align: center;
          padding: 24px;
        }

        .detail-footer {
          height: 24px;
        }
      `}</style>
    </div>
  );
}